import { ArrowUp, CalendarDays, Check, ChevronDown, ChevronRight, ChevronUp, Flame, Moon, Play, TrendingUp } from 'lucide-react'
import { useMemo, useState } from 'react'
import { ExerciseMedia } from '../../components/ExerciseMedia'
import { Modal } from '../../components/Modal'
import { useStrata } from '../../data/StrataProvider'
import { dietDayNumber, localDateKey, longLocalDate, weekdayInMexicoCity } from '../../domain/calendar'
import { overloadCycle } from '../../domain/overload'
import type { Exercise, WorkoutTemplate } from '../../domain/types'
import { ExerciseDetail } from '../library/ExerciseDetail'
import { WorkoutPreview } from './WorkoutPreview'

export function TodayPage({ onStart }: { onStart: (template: WorkoutTemplate) => void }) {
  const { state, toggleDietDay } = useStrata()
  const [expanded, setExpanded] = useState(false)
  const [selectedExercise, setSelectedExercise] = useState<Exercise>()
  const [selectedWeekday, setSelectedWeekday] = useState<number>()
  const weekday = weekdayInMexicoCity()
  const dateKey = localDateKey()
  const today = state.templates.find((item) => item.weekday === weekday)
  const next = [...state.templates].sort((a, b) => distance(a.weekday, weekday) - distance(b.weekday, weekday))[0]
  const entries = today ? (expanded ? today.entries : today.entries.slice(0, 4)) : []
  const dietCompleted = state.dietCheckIns.some((item) => item.date === dateKey)
  const dietDay = dietDayNumber(state.profile.dietStartedOn, dateKey)
  const dietWeek = dietDay ? Math.ceil(dietDay / 7) : 0
  const targets = state.profile.nutritionTargets
  const sessions = useMemo(() => [...state.sessions].sort((a, b) => b.startedAt.localeCompare(a.startedAt)), [state.sessions])
  const completedSessions = sessions.filter((session) => session.status !== 'in_progress')
  const draftSession = sessions.find((session) => session.status === 'in_progress')
  const draftTemplate = state.templates.find((template) => template.id === draftSession?.templateId)
  const recent = completedSessions.filter((session) => Date.now() - new Date(session.startedAt).getTime() < 7 * 86_400_000)
  const recovery = Math.min(96, 70 + recent.length * 4)
  const last = completedSessions[0]
  const selectedTemplate = state.templates.find((item) => item.weekday === selectedWeekday)
  const overload = overloadCycle(state.profile, state.preferences)

  return (
    <div className={`page today-page ${today ? '' : 'rest-day'}`}>
      <header className="page-header">
        <span className="eyebrow">{longLocalDate()}</span>
        <div className="title-row">
          <h1>Hoy</h1>
          <span className="metric-chip"><Flame size={13} fill="currentColor" /> {state.dietCheckIns.length}/{dietDay || '—'} días</span>
        </div>
      </header>

      <section className={`diet-card ${dietCompleted ? 'completed' : ''}`}>
        <div className="diet-heading">
          <div><span className="eyebrow">DIETA · SEMANA {dietWeek || '—'}</span><strong>{dietDay ? `Día ${dietDay}` : 'Sin fecha inicial'}</strong></div>
          <button aria-pressed={dietCompleted} onClick={() => toggleDietDay(dateKey)}><Check size={20} /></button>
        </div>
        <div className="macro-line">
          <span><b>{targets.calories}</b> kcal</span>
          <span><b>{targets.protein}</b> P</span>
          <span><b>{targets.carbohydrates}</b> C</span>
          <span><b>{targets.fat}</b> G</span>
        </div>
        <button className="diet-complete-action" onClick={() => toggleDietDay(dateKey)}>
          {dietCompleted ? 'Día completado · deshacer' : 'Marcar dieta de hoy como completada'}
        </button>
      </section>

      {overload.due && (
        <section className="overload-banner">
          <TrendingUp size={22} />
          <div><span className="eyebrow">SEMANA {overload.programWeek} · SOBRECARGA PROGRESIVA</span><strong>Esta semana toca avanzar.</strong><p>Intenta el incremento configurado o una repetición más sólo donde mantengas técnica y RIR.</p></div>
        </section>
      )}

      {draftSession && draftTemplate && (
        <button className="workout-draft-card" onClick={() => onStart(draftTemplate)}>
          <span><small>ENTRENAMIENTO EN CURSO</small><strong>{draftSession.name}</strong><em>{draftSession.sets.length} series ya guardadas</em></span>
          <b>Continuar <ChevronRight size={15} /></b>
        </button>
      )}

      {today ? (
        <section className="routine-section">
          <div className="routine-heading">
            <div><span className="eyebrow muted">ENTRENAMIENTO DE HOY</span><h2>{today.name}</h2></div>
            <span>{today.minutes} min</span>
          </div>

          <div className="exercise-sequence">
            {entries.map((entry, index) => {
              const exercise = state.exercises.find((item) => item.id === entry.exerciseId)!
              return (
                <button className="sequence-row" key={exercise.id} onClick={() => setSelectedExercise(exercise)}>
                  <span className="sequence-track"><i />{index !== entries.length - 1 && <b />}</span>
                  <ExerciseMedia exercise={exercise} />
                  <span className="sequence-copy">
                    <strong>{exercise.name}</strong>
                    <small>{entry.sets} {entry.sets === 1 ? 'serie' : 'series'} · {entry.minReps}–{entry.maxReps} {entry.repUnit === 'min' ? 'min' : 'reps'} · RIR {entry.targetRir}</small>
                  </span>
                  <span className="sequence-index">{String(index + 1).padStart(2, '0')}</span>
                </button>
              )
            })}
          </div>

          {today.entries.length > 4 && (
            <button className="text-action centered" onClick={() => setExpanded((value) => !value)}>
              {expanded ? <><ChevronUp size={16} /> Mostrar menos</> : <><ChevronDown size={16} /> Ver los {today.entries.length}</>}
            </button>
          )}
        </section>
      ) : (
        <section className="rest-hero">
          <div className="rest-orb"><Moon size={27} /></div>
          <span className="eyebrow">DOMINGO · OFF</span>
          <h2>Descanso real.</h2>
          <p>Camina si te apetece, movilidad ligera y recuperación. No hay trabajo compensatorio hoy.</p>
          <button className="next-session" onClick={() => setSelectedWeekday(next.weekday)}><span>Siguiente</span><strong>{next.fullName}</strong><em>{next.minutes} min <ChevronRight size={13} /></em></button>
        </section>
      )}

      <section className="week-plan">
        <div className="week-plan-heading"><div><span className="eyebrow">PLAN SEMANAL</span><h2>Previsualiza todo.</h2></div><CalendarDays size={20} /></div>
        <div className="week-strip">
          {weekDays.map((day) => {
            const template = state.templates.find((item) => item.weekday === day.weekday)
            return (
              <button key={day.weekday} className={weekday === day.weekday ? 'current' : ''} onClick={() => setSelectedWeekday(day.weekday)}>
                <span>{day.short}</span>
                <strong>{template?.name ?? 'OFF'}</strong>
                <small>{template ? `${template.entries.length} ejercicios` : 'Recuperación'}</small>
              </button>
            )
          })}
        </div>
      </section>

      <section className="today-context">
        <div><span>Recuperación</span><strong>{recovery}% <ArrowUp size={14} /></strong></div>
        <i />
        <div className="last-session"><span>Última</span><strong>{last ? `${last.name} · ${last.prCount} PR` : 'Aún sin sesiones'}</strong></div>
      </section>

      {today && <div className="sticky-page-action"><button className="primary-button" onClick={() => onStart(today)}><Play size={17} fill="currentColor" />{draftSession?.templateId === today.id ? `Continuar ${today.name}` : `Empezar ${today.name}`}</button></div>}

      {selectedExercise && (
        <Modal title={selectedExercise.name} onClose={() => setSelectedExercise(undefined)}>
          <ExerciseDetail exercise={selectedExercise} />
        </Modal>
      )}

      {selectedWeekday !== undefined && (
        <Modal title={selectedTemplate?.fullName ?? 'Domingo — Descanso'} onClose={() => setSelectedWeekday(undefined)} large>
          {selectedTemplate ? (
            <WorkoutPreview template={selectedTemplate} onStart={() => { setSelectedWeekday(undefined); onStart(selectedTemplate) }} />
          ) : (
            <div className="rest-preview"><div className="rest-orb"><Moon size={27} /></div><span className="eyebrow">RECUPERACIÓN</span><h2>Domingo — Off</h2><p>Caminar, movilidad ligera y comer suficiente. El objetivo es llegar al lunes con ganas de producir fuerza.</p></div>
          )}
        </Modal>
      )}
    </div>
  )
}

const weekDays = [
  { weekday: 1, short: 'LUN' },
  { weekday: 2, short: 'MAR' },
  { weekday: 3, short: 'MIÉ' },
  { weekday: 4, short: 'JUE' },
  { weekday: 5, short: 'VIE' },
  { weekday: 6, short: 'SÁB' },
  { weekday: 0, short: 'DOM' },
]

function distance(target: number, current: number) {
  const value = (target - current + 7) % 7
  return value === 0 ? 7 : value
}
